import { parseTimestamp } from '../uitilites/utility';

export const GPS_STALE_AFTER_MINUTES = 15;

export const APP_PRESENCE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
  { value: 'not_seen', label: 'Not seen' },
];

export const LAST_SEEN_WITHIN_FILTERS = [
  { value: null, label: 'Any time' },
  { value: 2, label: '≤ 2m' },
  { value: 5, label: '≤ 5m' },
  { value: 15, label: '≤ 15m' },
  { value: 60, label: '≤ 1h' },
  { value: 360, label: '≤ 6h' },
  { value: 1440, label: '≤ 24h' },
];

export const DUTY_STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'in_duty', label: 'On duty' },
  { value: 'not_in_duty', label: 'Off duty' },
  { value: 'pending', label: 'Pending' },
  { value: 'no_response', label: 'No response' },
];

export const INCIDENT_READINESS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'in_duty', label: 'Ready' },
  { value: 'pending', label: 'Pending' },
  { value: 'not_in_duty', label: 'Unavailable' },
  { value: 'no_response', label: 'No response' },
  { value: 'not_checked', label: 'Not checked' },
];

export const GPS_STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'usable', label: 'Usable' },
  { value: 'stale', label: 'Outdated' },
  { value: 'unavailable', label: 'Unavailable' },
];

export const MAX_ETA_FILTERS = [
  { value: null, label: 'Any ETA' },
  { value: 5, label: '≤ 5m' },
  { value: 10, label: '≤ 10m' },
  { value: 15, label: '≤ 15m' },
  { value: 30, label: '≤ 30m' },
  { value: 60, label: '≤ 1h' },
];

export const MAX_DISTANCE_FILTERS = [
  { value: null, label: 'Any distance' },
  { value: 100, label: '≤ 100m' },
  { value: 500, label: '≤ 500m' },
  { value: 1000, label: '≤ 1km' },
  { value: 5000, label: '≤ 5km' },
  { value: 10000, label: '≤ 10km' },
  { value: 25000, label: '≤ 25km' },
];

export const DEFAULT_PERSONNEL_AVAILABILITY_FILTERS = Object.freeze({
  app: 'all',
  seenWithin: null,
  duty: 'all',
  incident: 'all',
  gps: 'all',
  maxDistance: null,
  maxEta: null,
});

export const currentRoleAssignmentEntries = (item, assignmentMap = item?.meta?.roleAssignments || {}) => {
  const meta = item?.meta || {};
  if (!Object.prototype.hasOwnProperty.call(meta, 'scenarioRoles')) return Object.entries(assignmentMap || {});
  const declaredRoles = new Set((Array.isArray(meta.scenarioRoles) ? meta.scenarioRoles : [])
    .filter((definition) => definition && typeof definition === 'object')
    .map((definition) => String(definition.role || '').trim())
    .filter(Boolean));
  return Object.entries(assignmentMap || {}).filter(([role]) => declaredRoles.has(role));
};

const finiteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const elapsedCompactLabel = (timestamp, nowMs = Date.now()) => {
  const at = timestamp instanceof Date ? timestamp : parseTimestamp(timestamp);
  if (!at || Number.isNaN(at.getTime())) return '';
  const elapsedMs = Number(nowMs) - at.getTime();
  if (!Number.isFinite(elapsedMs)) return '';
  const minutes = Math.max(0, Math.floor(elapsedMs / 60000));
  if (minutes < 1) return '< 1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

export const mobilePresenceMeta = (presence, nowMs = Date.now()) => {
  const statusValue = String(presence?.status || '').trim().toLowerCase();
  const explicitlyOnline = presence === true
    || presence?.online === true
    || presence?.is_online === true
    || statusValue === 'online';
  const rawLastSeen = presence?.last_seen_at
    || presence?.lastSeenAt
    || presence?.last_seen
    || presence?.seen_at
    || null;
  const parsedLastSeen = rawLastSeen instanceof Date ? rawLastSeen : parseTimestamp(rawLastSeen);
  const hasLastSeen = !!parsedLastSeen && !Number.isNaN(parsedLastSeen.getTime());
  const rawValidUntil = presence?.valid_until || presence?.validUntil || null;
  const parsedValidUntil = rawValidUntil instanceof Date ? rawValidUntil : parseTimestamp(rawValidUntil);
  const hasValidUntil = !!parsedValidUntil && !Number.isNaN(parsedValidUntil.getTime());
  // A missed offline event must not leave a user live forever. When the server
  // supplies its heartbeat deadline, the snapshot is online only until then.
  const online = explicitlyOnline && (!hasValidUntil || parsedValidUntil.getTime() > Number(nowMs));

  if (online) {
    return {
      status: 'online',
      label: 'Online now',
      tone: 'online',
      online: true,
      lastSeenAt: hasLastSeen ? parsedLastSeen : null,
      validUntil: hasValidUntil ? parsedValidUntil : null,
      ageMinutes: 0,
    };
  }

  if (hasLastSeen) {
    const ageMinutes = Math.max(0, (Number(nowMs) - parsedLastSeen.getTime()) / 60000);
    return {
      status: 'offline',
      label: `Last seen ${elapsedCompactLabel(parsedLastSeen, nowMs)} ago`,
      tone: 'offline',
      online: false,
      lastSeenAt: parsedLastSeen,
      validUntil: hasValidUntil ? parsedValidUntil : null,
      ageMinutes: Number.isFinite(ageMinutes) ? ageMinutes : null,
    };
  }

  return {
    status: 'not_seen',
    label: 'App not seen',
    tone: 'muted',
    online: false,
    lastSeenAt: null,
    validUntil: hasValidUntil ? parsedValidUntil : null,
    ageMinutes: null,
  };
};

// How long a user has been on duty since the latest response. parseTimestamp
// keeps naive backend timestamps in UTC instead of interpreting them in the
// browser's local timezone.
export const dutyDurationLabel = (info, nowMs = Date.now()) => {
  if (info?.status !== 'in_duty' || !info?.responded_at) return '';
  const at = parseTimestamp(info.responded_at);
  if (!at || Number.isNaN(at.getTime())) return '';
  const ms = Number(nowMs) - at.getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return '< 1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

export const generalDutyStatus = (info) => {
  const status = String(info?.status || '').toLowerCase();
  if (status === 'in_duty' || status === 'not_in_duty' || status === 'pending' || status === 'no_response') return status;
  return 'no_response';
};

export const incidentReadinessStatus = (info, loadState = 'loaded') => {
  if (loadState === 'loading') return 'loading';
  if (loadState === 'unavailable') return 'unavailable_status';
  const status = String(info?.status || '').toLowerCase();
  if (status === 'in_duty' || status === 'not_in_duty' || status === 'pending' || status === 'no_response') return status;
  return 'not_checked';
};

export const dutyStatusMeta = (info) => {
  const status = generalDutyStatus(info);
  if (status === 'in_duty') return { status, label: 'On duty', tone: 'ready' };
  if (status === 'not_in_duty') return { status, label: 'Off duty', tone: 'danger' };
  if (status === 'pending') return { status, label: 'Pending', tone: 'pending' };
  return { status: 'no_response', label: 'No response', tone: 'muted' };
};

export const readinessStatusMeta = (info, loadState = 'loaded') => {
  const status = incidentReadinessStatus(info, loadState);
  if (status === 'in_duty') return { status, label: 'Ready', tone: 'ready' };
  if (status === 'not_in_duty') return { status, label: 'Unavailable', tone: 'danger' };
  if (status === 'pending') return { status, label: 'Pending', tone: 'pending' };
  if (status === 'no_response') return { status, label: 'No response', tone: 'muted' };
  if (status === 'loading') return { status, label: 'Checking…', tone: 'muted' };
  if (status === 'unavailable_status') return { status, label: 'Status unavailable', tone: 'danger' };
  return { status: 'not_checked', label: 'Not checked', tone: 'muted' };
};

const freshnessMinutes = (freshness) => {
  const value = String(freshness || '').trim().toLowerCase();
  if (!value || value === 'location unavailable') return null;
  if (value === 'live' || value === 'now' || value === '< 1m ago') return 0;
  const match = value.match(/^(\d+(?:\.\d+)?)\s*(m|min|mins|minute|minutes|h|hr|hrs|hour|hours|d|day|days)\s*ago$/);
  if (!match) return null;
  const amount = Number(match[1]);
  if (!Number.isFinite(amount)) return null;
  if (match[2].startsWith('h')) return amount * 60;
  if (match[2].startsWith('d')) return amount * 1440;
  return amount;
};

export const readinessLocationAgeMinutes = (info, nowMs = Date.now()) => {
  const rawTimestamp = info?.location_updated_at || info?.location_timestamp;
  if (rawTimestamp) {
    const parsed = parseTimestamp(rawTimestamp);
    if (parsed && !Number.isNaN(parsed.getTime())) {
      return Math.max(0, (nowMs - parsed.getTime()) / 60000);
    }
  }
  return freshnessMinutes(info?.location_freshness);
};

export const readinessLocation = (info, nowMs = Date.now()) => {
  const distance = finiteNumber(info?.distance_m);
  if (distance === null) {
    return { text: 'Location unavailable', usable: false, stale: false, ageMinutes: null, distance: null };
  }
  const ageMinutes = readinessLocationAgeMinutes(info, nowMs);
  const stale = typeof info?.location_is_stale === 'boolean'
    ? info.location_is_stale
    : ageMinutes === null || ageMinutes > GPS_STALE_AFTER_MINUTES;
  const freshness = String(info?.location_freshness || '').trim();
  if (stale) {
    return {
      text: `GPS outdated${freshness && freshness.toLowerCase() !== 'location unavailable' ? ` · ${freshness}` : ''}`,
      usable: false,
      stale: true,
      ageMinutes,
      distance,
    };
  }
  const distanceText = distance < 1000 ? `${Math.round(distance)} m away` : `${(distance / 1000).toFixed(1)} km away`;
  const eta = finiteNumber(info?.eta_minutes);
  const freshnessText = ageMinutes <= 2 ? 'GPS live' : `GPS ${Math.max(1, Math.round(ageMinutes))}m ago`;
  return {
    text: `${distanceText}${eta !== null ? ` · ${Math.max(1, Math.round(eta))} min ETA` : ''} · ${freshnessText}`,
    usable: true,
    stale: false,
    ageMinutes,
    distance,
  };
};

export const personnelAssignmentWarnings = (dutyInfo, readinessInfo, readinessLoadState = 'loaded') => {
  const warnings = [];
  const duty = generalDutyStatus(dutyInfo);
  const readiness = incidentReadinessStatus(readinessInfo, readinessLoadState);

  if (duty === 'not_in_duty') warnings.push('General duty status is Off duty.');
  else if (duty === 'pending') warnings.push('General duty response is still pending.');
  else if (duty !== 'in_duty') warnings.push('No current general duty response.');

  if (readiness === 'pending') warnings.push('Incident readiness is still pending.');
  else if (readiness === 'not_in_duty') warnings.push('The person marked themselves unavailable for this incident.');
  else if (readiness === 'no_response') warnings.push('There is no response to this incident readiness check.');
  else if (readiness === 'not_checked') warnings.push('This person has not been checked for this incident.');
  else if (readiness === 'loading') warnings.push('Incident readiness is still loading.');
  else if (readiness === 'unavailable_status') warnings.push('Incident readiness status could not be loaded.');

  return warnings;
};

export const dutyRank = (info) => {
  const status = generalDutyStatus(info);
  return status === 'in_duty' ? 0 : status === 'pending' ? 1 : status === 'no_response' ? 2 : 3;
};

export const readinessRank = (info, loadState = 'loaded') => {
  const status = incidentReadinessStatus(info, loadState);
  if (status === 'in_duty') return 0;
  if (status === 'pending') return 1;
  if (status === 'no_response' || status === 'not_checked' || status === 'loading') return 2;
  return 3;
};

export const usableReadinessDistance = (info, nowMs = Date.now()) => {
  const location = readinessLocation(info, nowMs);
  return location.usable ? location.distance : Number.MAX_SAFE_INTEGER;
};

export const passesPersonnelAvailabilityFilters = ({
  presence,
  dutyInfo,
  readinessInfo,
  readinessLoadState = 'loaded',
  filters = DEFAULT_PERSONNEL_AVAILABILITY_FILTERS,
  nowMs = Date.now(),
} = {}) => {
  const activeFilters = { ...DEFAULT_PERSONNEL_AVAILABILITY_FILTERS, ...(filters || {}) };
  const presenceMeta = mobilePresenceMeta(presence, nowMs);
  const presenceStatus = presenceMeta.status;
  if (activeFilters.app !== 'all' && presenceStatus !== activeFilters.app) return false;

  const seenWithin = finiteNumber(activeFilters.seenWithin);
  if (seenWithin !== null) {
    // A currently online mobile session is necessarily recent, including the
    // legacy boolean/set fallback that cannot provide a last-seen timestamp.
    if (!presenceMeta.online && (
      presenceMeta.status !== 'offline'
      || presenceMeta.ageMinutes === null
      || presenceMeta.ageMinutes > seenWithin
    )) return false;
  }

  const dutyStatus = generalDutyStatus(dutyInfo);
  if (activeFilters.duty !== 'all' && dutyStatus !== activeFilters.duty) return false;

  const readinessStatus = incidentReadinessStatus(readinessInfo, readinessLoadState);
  if (activeFilters.incident !== 'all' && readinessStatus !== activeFilters.incident) return false;

  const location = readinessLocation(readinessInfo, nowMs);
  if (activeFilters.gps === 'live' && !(location.usable && location.ageMinutes !== null && location.ageMinutes <= 2)) return false;
  if (activeFilters.gps === 'usable' && !location.usable) return false;
  if (activeFilters.gps === 'stale' && !location.stale) return false;
  if (activeFilters.gps === 'unavailable' && (location.usable || location.stale)) return false;

  const maxDistance = finiteNumber(activeFilters.maxDistance);
  if (maxDistance !== null && (!location.usable || location.distance === null || location.distance > maxDistance)) return false;

  const maxEta = finiteNumber(activeFilters.maxEta);
  if (maxEta !== null) {
    const eta = finiteNumber(readinessInfo?.eta_minutes);
    if (!location.usable || eta === null || eta > maxEta) return false;
  }

  return true;
};
